import styled, { css } from 'styled-components';

import { colors, fonts, radii } from '@gbs-monorepo-packages/styles';

import { BaseLoading } from '../BaseLoading';
import { BaseSlot } from '../BaseSlot';

interface IOptionsButtonProps {
  $noPaddingRight: boolean;
  $styleType?:
    | 'accept'
    | 'admin'
    | 'decline'
    | 'default'
    | 'ghost'
    | 'outline'
    | 'secondary'
    | 'text';
  $textTransform: 'capitalize' | 'lowercase' | 'uppercase';
  $fontWeight: number;
}

export const buttonBaseCss = css`
  align-items: center;
  border-radius: ${radii.md};
  cursor: pointer;
  display: inline-flex;
  font-family: inherit;
  font-size: 0.8125rem;
  font-weight: 600;
  gap: 0.4rem;
  justify-content: center;
  line-height: 1.2;
  text-decoration: none;

  &:hover {
    filter: brightness(0.9);
  }
`;

export const buttonPrimaryCss = css`
  ${buttonBaseCss}
  background: ${colors.blue300};
  border: none;
  color: ${colors.white300};

  &:hover {
    background: ${colors.blue400};
    filter: none;
  }
`;

export const buttonOutlineCss = css`
  ${buttonBaseCss}
  background: ${colors.white300};
  border: 1px solid ${colors.inputBorderLight};
  color: ${colors.text};

  &:hover {
    background: ${colors.lightGray100};
    filter: none;
  }
`;

export const buttonSecondaryCss = css`
  ${buttonBaseCss}
  background: ${colors.background};
  border: none;
  color: ${colors.textLight};
`;

export const buttonGhostCss = css`
  ${buttonBaseCss}
  background: transparent;
  border: none;
  color: ${colors.textLight};

  &:hover {
    background: ${colors.backgroundHover};
    filter: none;
  }
`;

export const buttonPillCss = css`
  ${buttonBaseCss}
  background: ${colors.lightGray100};
  border: none;
  border-radius: ${radii.full};
  font-size: 0.75rem;
  font-weight: 500;
  white-space: nowrap;

  &:hover {
    background: ${colors.backgroundHover};
    filter: none;
  }
`;

const buttonStyle = css<IOptionsButtonProps>`
  all: unset;
  align-items: center;
  border-radius: ${radii.md};
  cursor: pointer;
  display: inline-flex;
  font-family: inherit;
  font-size: 0.875rem;
  justify-content: center;
  line-height: 1.5;
  padding: 0.5rem 1rem;
  font-weight: 700;

  &:hover {
    filter: brightness(0.9);
  }

  &[disabled] {
    cursor: not-allowed;
    opacity: unset;
    background-color: ${colors.blue50};
  }

  ${(props) =>
    props.$noPaddingRight &&
    css`
      padding-right: 0.25rem;
    `}

  ${(props) =>
    props.$textTransform &&
    css`
      text-transform: ${props.$textTransform};
    `}

  ${(props) =>
    props.$fontWeight &&
    css`
      text-transform: ${props.$fontWeight};
    `}

  ${(props) => {
    switch (props.$styleType) {
      case 'outline':
        return css`
          background-color: ${colors.white300};
          border: 0.5px solid ${colors.button};
          color: ${colors.button};
          &[disabled] {
            opacity: 0.2;
          }
        `;
      case 'secondary':
        return css`
          background-color: ${colors.background};
          color: ${colors.textLight};
          &[disabled] {
            opacity: 0.25;
          }
        `;
      case 'ghost':
        return css`
          background-color: transparent;
          color: ${colors.textLight};
          &[disabled] {
            opacity: 0.25;
          }
        `;
      case 'text':
        return css`
          background-color: ${colors.white300};
          color: ${colors.button};
          &[disabled] {
            opacity: 0.25;
          }
        `;
      case 'accept':
        return css`
          background-color: ${colors.statusSuccess};
          color: ${colors.statusTextSuccess};
          &[disabled] {
            opacity: 0.25;
          }
        `;
      case 'decline':
        return css`
          background-color: ${colors.statusError};
          color: ${colors.statusTextError};
          &[disabled] {
            opacity: 0.25;
          }
        `;
      case 'admin':
        return css`
          background-color: ${colors.blue275};
          font-family: ${fonts.admin};
          color: ${colors.white300};
          &[disabled] {
            opacity: 0.25;
          }
        `;
      case 'default':
        return css`
          background-color: ${colors.button};
          color: ${colors.white300};
        `;
      default:
        return css`
          background-color: ${colors.button};
          color: ${colors.white300};
        `;
    }
  }}
`;

export const DefaultButton = styled.button<IOptionsButtonProps>`
  ${buttonStyle};
`;

export const SlotButton = styled(BaseSlot)<IOptionsButtonProps>`
  ${buttonStyle};
`;

export const Loading = styled(BaseLoading)`
  height: 1.3125rem;
  width: 1.3125rem;
`;
